import { APPROVAL_KIND_LABEL, type ApprovalKind } from '../../../services/vehicleTransferService';

/**
 * V3-069: a transfer's History in plain language. No setting names, event codes or raw errors
 * (LANG-06), and no repeated words.
 */

const EVENT_TITLE: Record<string, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ALL_APPROVED: 'All approvals in',
  CLOSED_REJECTED: 'Closed as rejected',
  REASSIGNED: 'Approver reassigned',
  CANCELLED: 'Cancelled',
  COMPLETED: 'Marked complete',
  PDF_ATTACHED: 'Record attached to the unit',
  PDF_FAILED: "The record PDF couldn't be made",
};

/** What an email was for, as the start of a sentence. */
const EMAIL_WHAT: Record<string, { one: string; many: string }> = {
  'approval-request': { one: 'Approval email', many: 'Approval emails' },
  reassigned: { one: 'Approval email', many: 'Approval emails' },
  approved: { one: 'Approved record email', many: 'Approved record emails' },
  rejected: { one: 'Rejection email', many: 'Rejection emails' },
};

function emailWhat(purpose: unknown, count: number): string {
  const what = EMAIL_WHAT[String(purpose ?? '')] ?? { one: 'Email', many: 'Emails' };
  return count === 1 ? what.one : what.many;
}

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

export interface TransferHistoryEvent {
  eventType: string;
  payload?: unknown;
  actor?: string | null;
}

/** The line shown for one History entry: a title and an optional detail, both plain language. */
export function transferEventText(event: TransferHistoryEvent): { title: string; detail: string } {
  const p = (event.payload ?? {}) as Record<string, unknown>;
  const to = Array.isArray(p.to) ? p.to.length : 0;
  const actor = event.actor ?? '';

  switch (event.eventType) {
    case 'EMAIL_SKIPPED': {
      const reason = String(p.reason ?? '');
      const off = /EMAIL_ENABLED|email-off/i.test(reason);
      const why = off
        ? 'email is off on this environment.'
        : /recipient|no-recipients/i.test(reason)
          ? 'nobody to send it to has an email address.'
          : 'it could not be sent.';
      return { title: `${emailWhat(p.purpose, Math.max(to, 2))} not sent – ${why}`, detail: '' };
    }
    case 'EMAIL_SENT':
      return { title: `${emailWhat(p.purpose, to || 1)} sent${to ? ` to ${people(to)}` : ''}`, detail: '' };
    case 'EMAIL_FAILED':
      return { title: `${emailWhat(p.purpose, 1)} couldn't be sent${to ? ` to ${people(to)}` : ''}`, detail: '' };
    case 'APPROVED':
    case 'REJECTED': {
      const kind = APPROVAL_KIND_LABEL[p.kind as ApprovalKind];
      const comment = p.comment ? `“${String(p.comment)}”` : '';
      return { title: EVENT_TITLE[event.eventType], detail: [actor, kind, comment].filter(Boolean).join(' · ') };
    }
    case 'PDF_FAILED':
      return { title: EVENT_TITLE.PDF_FAILED, detail: 'Use Attach the Record PDF to try again.' };
    default:
      return { title: EVENT_TITLE[event.eventType] ?? 'Updated', detail: actor };
  }
}
